import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import FilterDrawer from '../../../shared/FilterDrawer';
import CancelButton from '../../../shared/CancelButton';
import SaveButton from '../../../shared/SaveButton';
import RequiredMark from '../../../shared/RequiredMark';
import ToggleSwitch from '../../../shared/ToggleSwitch';
import RichText from '../../../shared/RichText';
import MergeFieldRichTextField, {
  mergeFieldsForTemplate, insertSubjectToken, renderMergePreview,
} from '../../../shared/MergeFieldRichTextField';
import '../../../shared/StyledInput.css';

const fieldLabelStyle = { fontSize: 12, color: '#5a6b85', textTransform: 'uppercase', letterSpacing: 0.3, display: 'block', marginBottom: 4 };
const fieldWrapStyle = { marginBottom: 14 };

// Add/Edit form for an Email Template (story #236) — a separate drawer
// rather than FieldHelpTable/ActivityOutcomeTable's inline row-expand,
// since the rich text editor, merge-field buttons, and live preview need
// more room than a table row comfortably gives. Same FilterDrawer shell
// LogActivityDrawer.js uses.
function EmailTemplateDrawer({ open, onOpenChange, editingTemplate, onSaved }) {
  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [forQuotes, setForQuotes] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const subjectInputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    if (editingTemplate) {
      setName(editingTemplate.name || '');
      setSubject(editingTemplate.subject || '');
      setBody(editingTemplate.body || '');
      setIsActive(editingTemplate.is_active !== false);
      setForQuotes(!!editingTemplate.for_quotes);
    } else {
      setName('');
      setSubject('');
      setBody('');
      setIsActive(true);
      setForQuotes(false);
    }
    setError('');
  }, [open, editingTemplate]);

  const canSave = !!(name.trim() && subject.trim() && body.trim());
  // A built-in template (issue #241/#332) keeps what it's for, and can't be
  // deactivated. A template used for Quotes offers the Quote merge fields.
  const systemKey = editingTemplate?.system_key || null;
  const mergeFields = mergeFieldsForTemplate(forQuotes);

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setError('');
    try {
      const payload = { name: name.trim(), subject: subject.trim(), body, for_quotes: forQuotes };
      let res;
      if (editingTemplate) {
        res = await axios.put(`/api/email-templates/${editingTemplate.id}`, payload);
        // Active/inactive is a separate endpoint from the field edit (same
        // split as ActivityOutcome) — only called when the toggle actually
        // changed, so an unrelated edit never re-touches is_active.
        if (isActive !== (editingTemplate.is_active !== false)) {
          res = await axios.patch(`/api/email-templates/${editingTemplate.id}/${isActive ? 'reactivate' : 'deactivate'}`);
        }
      } else {
        res = await axios.post('/api/email-templates', payload);
      }
      onSaved(res.data.data);
      onOpenChange(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Save failed');
    }
    setSaving(false);
  };

  const { previewSubject, previewBodyHtml } = renderMergePreview(subject, body);

  return (
    <FilterDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={editingTemplate ? 'Edit Email Template' : 'Add Email Template'}
      description="Name, subject, and body — merge fields pull from the Customer/Company/Sender record when this template is sent."
      footer={(
        <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
          <CancelButton onClick={() => onOpenChange(false)} disabled={saving} />
          <SaveButton onClick={handleSave} loading={saving} disabled={!canSave} type="button" />
        </div>
      )}
    >
      <div style={fieldWrapStyle}>
        <label style={fieldLabelStyle}>Name<RequiredMark /></label>
        <input
          className="styled-input"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Pricelist Request Reply"
          maxLength={100}
          style={{ width: '100%', fontSize: 14 }}
        />
      </div>

      <div style={fieldWrapStyle}>
        <label style={fieldLabelStyle}>Subject<RequiredMark /></label>
        <input
          ref={subjectInputRef}
          className="styled-input"
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="e.g. Your requested pricelist from {{company_name}}"
          maxLength={255}
          style={{ width: '100%', fontSize: 14 }}
        />
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
          {mergeFields.map((field) => (
            <button
              key={field.key}
              type="button"
              className="merge-field-btn"
              onClick={() => insertSubjectToken(subjectInputRef.current, field, subject, setSubject)}
              title={`Insert ${field.label}`}
            >
              + {field.label}
            </button>
          ))}
        </div>
      </div>

      <div style={fieldWrapStyle}>
        <label style={fieldLabelStyle}>Body<RequiredMark /></label>
        <MergeFieldRichTextField
          value={body}
          onChange={setBody}
          mergeFields={mergeFields}
          placeholder="Email body..."
        />
      </div>

      {!systemKey && (
        <div style={fieldWrapStyle}>
          <ToggleSwitch checked={forQuotes} onChange={setForQuotes} label="Used for Quotes" />
          <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
            Sent from a Quote with its PDF attached, and offered as a Payment Term's Quote Email Template.
          </div>
        </div>
      )}

      {!systemKey && (
        <div style={fieldWrapStyle}>
          <ToggleSwitch checked={isActive} onChange={setIsActive} label="Active" />
        </div>
      )}

      <div style={fieldWrapStyle}>
        <label style={fieldLabelStyle}>Preview (sample data)</label>
        <div style={{ border: '1px solid #e3e7ee', borderRadius: 6, padding: '10px 12px', background: '#f8fafc' }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
            {previewSubject || <span style={{ color: '#aab2c0', fontWeight: 400 }}>(no subject yet)</span>}
          </div>
          {previewBodyHtml ? (
            <RichText html={previewBodyHtml} style={{ fontSize: 14 }} />
          ) : (
            <span style={{ color: '#aab2c0', fontSize: 14 }}>(no body yet)</span>
          )}
        </div>
      </div>

      {error && <div style={{ color: 'red', fontSize: 13, marginTop: 8 }}>{error}</div>}
    </FilterDrawer>
  );
}

export default EmailTemplateDrawer;
